import React from 'react';
import { CRow, CCol } from '@coreui/react-pro';
import { Modal } from 'react-bootstrap';
import { CXButton, CXTextInput, CXDropDown } from '../../../components/CXForm';

type Option = { value: string; label: string };

type Props = {
    show: boolean;
    onHide: () => void;

    selectedStatus: string;
    setSelectedStatus: (v: string) => void;

    availableStatusOptions: Option[];

    statusRemark: string;
    setStatusRemark: (v: string) => void;

    statusFiles: File[];
    statusUploading: boolean;
    statusSaving: boolean;
    statusError: string;
    statusRemarkHasError: boolean;

    onStatusFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onStatusDrop: (e: React.DragEvent<HTMLDivElement>) => void;
    onStatusDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
    onRemoveStatusFile: (index: number) => void;

    onUpdateClick: () => void;
};

export default function StatusChangeModal(props: Props) {
    const {
        show,
        onHide,
        selectedStatus,
        setSelectedStatus,
        availableStatusOptions,
        statusRemark,
        setStatusRemark,
        statusFiles,
        statusUploading,
        statusSaving,
        statusError,
        statusRemarkHasError,
        onStatusFileChange,
        onStatusDrop,
        onStatusDragOver,
        onRemoveStatusFile,
        onUpdateClick,
    } = props;

    return (
        <Modal
            className="statusModal"
            show={show}
            onHide={onHide}
            size="lg"
        >
            <Modal.Header className="modal-header" closeButton>
                <Modal.Title>Status change</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <CRow>
                    <CCol xs={3} style={{ paddingTop: 6 }}>
                        <span
                            style={{
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: '24px',
                                color: '#4C4C4C',
                            }}
                        >
                            Status
                        </span>
                    </CCol>
                    <CCol xs={9}>
                        <CXDropDown
                            value={selectedStatus}
                            placeholder="Status"
                            options={availableStatusOptions}
                            onChange={setSelectedStatus}
                        />
                    </CCol>
                </CRow>

                {selectedStatus && (
                    <>
                        <hr style={{ margin: '24px 0' }} />
                        <CRow>
                            <CCol xs={3} style={{ paddingTop: 6 }}>
                                <span
                                    style={{
                                        fontWeight: 400,
                                        fontSize: 16,
                                        lineHeight: '24px',
                                        color: '#4C4C4C',
                                    }}
                                >
                                    Remarks (Optional)
                                </span>
                            </CCol>
                            <CCol xs={9}>
                                <div className={statusRemarkHasError ? 'errorBorder' : ''}>
                                    <CXTextInput
                                        placeholder={'Remarks (Optional)'}
                                        value={statusRemark}
                                        onChange={(value) => {
                                            setStatusRemark(value);
                                        }}
                                        zIndex={1029}
                                    />
                                </div>
                                {statusError && (
                                    <div
                                        style={{
                                            color: '#DC3545',
                                            fontSize: 14,
                                            marginTop: 8,
                                            lineHeight: '18px',
                                        }}
                                    >
                                        {statusError}
                                    </div>
                                )}

                                <div
                                    onDrop={onStatusDrop}
                                    onDragOver={onStatusDragOver}
                                    style={{
                                        border: '1px dashed #D8D8D8',
                                        borderRadius: 4,
                                        padding: '24px 16px',
                                        textAlign: 'center',
                                        marginTop: 16,
                                        marginBottom: 16,
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 14,
                                            lineHeight: '20px',
                                            color: '#4C4C4C',
                                            marginBottom: 12,
                                        }}
                                    >
                                        Drop a file here to upload
                                    </div>
                                    <label
                                        style={{
                                            display: 'inline-block',
                                            padding: '8px 16px',
                                            borderRadius: 4,
                                            border: '1px solid #116F9A',
                                            color: '#116F9A',
                                            fontSize: 14,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Or choose files{' '}
                                        <input
                                            type="file"
                                            multiple
                                            style={{ display: 'none' }}
                                            onChange={onStatusFileChange}
                                            disabled={statusUploading}
                                        />
                                    </label>
                                </div>

                                <div
                                    style={{
                                        fontWeight: 400,
                                        fontSize: 16,
                                        lineHeight: '24px',
                                        color: '#4C4C4C',
                                        marginBottom: 4,
                                    }}
                                >
                                    Uploaded files {statusUploading && '(uploading...)'}
                                </div>
                                {statusFiles.length === 0 ? (
                                    <div
                                        style={{
                                            width: '100%',
                                            minHeight: 40,
                                            borderRadius: 4,
                                            border: '1px solid #E6E6E6',
                                            background: '#F5F7F8',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '0 12px',
                                            fontSize: 14,
                                            color: '#808285',
                                        }}
                                    >
                                        No files uploaded
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            width: '100%',
                                            borderRadius: 4,
                                            border: '1px solid #E6E6E6',
                                            background: '#F5F7F8',
                                            padding: '8px 12px',
                                        }}
                                    >
                                        {statusFiles.map((file, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '4px 0',
                                                    fontSize: 14,
                                                    color: '#000',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        flex: 1,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {file.name}
                                                </span>
                                                <button
                                                    onClick={() => onRemoveStatusFile(index)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#DC3545',
                                                        cursor: 'pointer',
                                                        fontSize: 14,
                                                        marginLeft: 8,
                                                    }}
                                                    disabled={statusUploading}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CCol>
                        </CRow>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="modal-footer">
                <div style={{ bottom: '40px', right: '256px', width: '200px' }}>
                    <CXButton
                        outline
                        text="Cancel"
                        onClick={onHide}
                        fontSize="16px"
                        width="100%"
                        disabled={statusUploading || statusSaving}
                    />
                </div>
                <div style={{ bottom: '40px', right: '40px', width: '200px' }}>
                    <CXButton
                        text={statusUploading || statusSaving ? 'Updating...' : 'Update'}
                        onClick={onUpdateClick}
                        fontSize="16px"
                        width="100%"
                        disabled={statusUploading || statusSaving || !selectedStatus}
                    />
                </div>
            </Modal.Footer>
        </Modal>
    );
}
